import { z } from "zod";
import { SessionPolicy } from "./schemas/auth.ts";
import { COMPANY_STATUSES } from "./enums.ts";

/** the company-ingredient statuses an import may set (expiring / expired are derived from the certificate, never imported) */
export const IMPORTABLE_INGREDIENT_STATUSES = ["pending", "under_review", "approved", "rejected"] as const;

/** the platform's company ↔ plant link statuses (the same words as CompanyPlantLinkInput) */
export const PLANT_LINK_STATUSES = ["pending", "linked", "ended"] as const;

/**
 * Typed settings registry. Every configurable threshold, cadence and gate is a
 * key here with its Zod schema and its default. Defaults live in code so a
 * fresh install runs unconfigured; the database holds only overrides.
 * (docs/DATA-MODEL.md → Configuration facilities; CONVENTIONS.md → Configuration vs invariants)
 */
export interface SettingDef<T extends z.ZodTypeAny = z.ZodTypeAny> {
  key: string;
  group: string;
  label: string;
  description: string;
  schema: T;
  default: z.infer<T>;
  /** Names the consequence for money/kosher-affecting settings. Shown under the field. */
  consequence?: string;
}

function def<T extends z.ZodTypeAny>(d: SettingDef<T>): SettingDef<T> {
  return d;
}

export const SETTINGS = {
  "session.policy.internal": def({
    key: "session.policy.internal",
    group: "Sessions",
    label: "Staff session",
    description: "Absolute lifetime and idle timeout for CRC staff sessions.",
    schema: SessionPolicy,
    default: { absoluteHours: 12, idleMinutes: 60 },
  }),
  "session.policy.company": def({
    key: "session.policy.company",
    group: "Sessions",
    label: "Company session",
    description: "Absolute lifetime and idle timeout for company users.",
    schema: SessionPolicy,
    default: { absoluteHours: 24 * 7, idleMinutes: 8 * 60 },
  }),
  "session.policy.location_user": def({
    key: "session.policy.location_user",
    group: "Sessions",
    label: "Facility user session",
    description: "Absolute lifetime and idle timeout for facility / location users.",
    schema: SessionPolicy,
    default: { absoluteHours: 24 * 7, idleMinutes: 8 * 60 },
  }),
  "session.policy.mashgiach": def({
    key: "session.policy.mashgiach",
    group: "Sessions",
    label: "Mashgiach session",
    description: "Mashgichim work offline in the field; 30 days with no idle timeout by default.",
    schema: SessionPolicy,
    default: { absoluteHours: 24 * 30, idleMinutes: 0 },
  }),
  "invite.expiry_days": def({
    key: "invite.expiry_days",
    group: "Sessions",
    label: "Invite validity (days)",
    description: "How long an invitation link stays usable.",
    schema: z.number().int().min(1).max(90),
    default: 14,
  }),
  "gate.loc_issuance.min_stage": def({
    key: "gate.loc_issuance.min_stage",
    group: "Onboarding gates",
    label: "Stage that unlocks certificate issuance",
    description: "A certificate may be issued once the onboarding pipeline reaches this stage, or once the company is certified.",
    schema: z.string(),
    default: "mashgiach_loc_setup",
    consequence: "Issuing earlier lets a certificate exist before the mashgiach and location setup is complete.",
  }),
  "gate.submissions.min_stage": def({
    key: "gate.submissions.min_stage",
    group: "Onboarding gates",
    label: "Stage that unlocks ingredient, product and plant submission",
    description: "Companies may submit ingredients, products and plants from this onboarding stage onwards.",
    schema: z.string(),
    default: "under_review",
  }),
  "ingredients.cert_expiry_warning_days": def({
    key: "ingredients.cert_expiry_warning_days",
    group: "Ingredients",
    label: "Certificate expiry warning (days)",
    description: "An ingredient certificate expiring within this many days shows as expiring. It flags; it never blocks.",
    schema: z.number().int().min(1).max(730),
    default: 180,
  }),
  "ingredients.agency_lookup_map": def({
    key: "ingredients.agency_lookup_map",
    group: "Ingredients",
    label: "Agency word → agency lookup",
    description: "Which certifying agency's public records the ingredient page's 'Check with the agency' button reads for an agency word on a master ingredient or its agency id. Keys are agency words, lower-case — the whole word as it appears on masters, or a word inside it (ou, ok, kof-k); values are the lookups the platform has: ou (OU letter of certification by UKD), kofk (Kof-K product search by UKD), ok (OK Kosher's DigitalKosher K-ID). A word with no entry is shown as not checkable.",
    schema: z.record(z.string(), z.enum(["ou", "kofk", "ok"])),
    default: { ou: "ou", "orthodox union": "ou", ok: "ok", "ok kosher": "ok", "ok kosher certification": "ok", "kof-k": "kofk", "kof k": "kofk", kofk: "kofk", kof_k: "kofk" } as Record<string, "ou" | "kofk" | "ok">,
  }),
  "ingredients.agency_pull_job": def({
    key: "ingredients.agency_pull_job",
    group: "Ingredients",
    label: "Pull agency records and certificates in the background",
    description: "On: the scheduled job walks master ingredients that carry an agency's own id (OU UKD, OK K-ID) and have not been pulled yet, a batch every ten minutes, filling their blanks from the agency's record and attaching the agency's certificate. Off: only the ingredient page's own pull and the Ingredients page's 'run a batch' button pull. Kof-K ids are skipped either way — the agency refuses requests from a server.",
    schema: z.boolean(),
    default: false,
  }),
  "ingredients.agency_pull_batch": def({
    key: "ingredients.agency_pull_batch",
    group: "Ingredients",
    label: "Agency pull batch size",
    description: "How many master ingredients one run of the agency pull takes on (the job, or the button). Each master costs the Worker up to three outside requests (record, certificate, storage); the Workers Free plan allows fifty a run, so keep this at fifteen there and raise it on a paid plan.",
    schema: z.number().int().min(1).max(300),
    default: 15,
  }),
  "ingredients.mashgiach_cover_recency_days": def({
    key: "ingredients.mashgiach_cover_recency_days",
    group: "Ingredients",
    label: "Cover recency for field requests (days)",
    description: "A mashgiach may file a request for a location he covered within this many days, on top of standing assignments.",
    schema: z.number().int().min(1).max(365),
    default: 90,
  }),
  "billing.overdue_grace_hours": def({
    key: "billing.overdue_grace_hours",
    group: "Billing",
    label: "Overdue hold grace period (hours)",
    description: "Time between warning a company and the hold becoming active.",
    schema: z.number().int().min(1).max(24 * 30),
    default: 48,
    consequence: "When the hold activates, new production scheduling is blocked for that company.",
  }),
  "scheduling.windows_ahead_days": def({
    key: "scheduling.windows_ahead_days",
    group: "Scheduling",
    label: "Generate windows ahead (days)",
    description: "How far ahead recurring schedule windows are generated, so next week's assignments are visible before the week starts.",
    schema: z.number().int().min(7).max(365),
    default: 35,
  }),
  "scheduling.weekly_visit_list_day": def({
    key: "scheduling.weekly_visit_list_day",
    group: "Scheduling",
    label: "Weekly visit list day",
    description: "Day of the week (0 = Sunday) on which each mashgiach's facility visit list for the coming week is sent.",
    schema: z.number().int().min(0).max(6),
    default: 0,
  }),
  "accounts.contract_warning_days": def({
    key: "accounts.contract_warning_days",
    group: "Onboarding gates",
    label: "Contract expiry warning (days)",
    description: "A certified company's contract ending within this many days is surfaced to its coordinator. Expiry never suspends a company by itself.",
    schema: z.number().int().min(1).max(365),
    default: 60,
  }),
  "locations.authorization_stalled_days": def({
    key: "locations.authorization_stalled_days",
    group: "Scheduling",
    label: "Authorization stalled after (days at one stage)",
    description: "A location authorization that has not moved stage for this many days is surfaced as stalled.",
    schema: z.number().int().min(1).max(365),
    default: 14,
  }),
  "scheduling.cover_needed_ahead_days": def({
    key: "scheduling.cover_needed_ahead_days",
    group: "Scheduling",
    label: "Cover needed — look-ahead (days)",
    description: "Facility windows starting within this many days with no mashgiach are surfaced to the coordinators.",
    schema: z.number().int().min(1).max(60),
    default: 7,
  }),
  "compliance.issue_overdue_days": def({
    key: "compliance.issue_overdue_days",
    group: "Compliance",
    label: "Issue overdue after (days open)",
    description: "An issue open longer than this is surfaced daily to its owner and the company's coordinator, on top of any escalation rule.",
    schema: z.number().int().min(1).max(365),
    default: 14,
  }),
  "events.stale_after_days": def({
    key: "events.stale_after_days",
    group: "Events",
    label: "Stale booking after (days past the date)",
    description: "An event still at 'booked' this many days after its date is surfaced as stale rather than buried.",
    schema: z.number().int().min(0).max(90),
    default: 2,
  }),
  "billing.mileage_rate": def({
    key: "billing.mileage_rate",
    group: "Billing",
    label: "Mileage rate (per mile)",
    description: "Applied to the miles a mashgiach submits on an invoice.",
    schema: z.number().min(0).max(10),
    default: 0.67,
    consequence: "Changes what mashgichim are paid per mile from the next invoice on.",
  }),
  "billing.late_billing_days": def({
    key: "billing.late_billing_days",
    group: "Billing",
    label: "Late billing window (days)",
    description: "A mashgiach invoice submitted more than this many days after the inspection is flagged as late (derived, never stored).",
    schema: z.number().int().min(1).max(365),
    default: 14,
  }),
  "billing.po_stale_after_days": def({
    key: "billing.po_stale_after_days",
    group: "Billing",
    label: "Open PO surfaces after (days)",
    description: "An open purchase order with no production scheduled against it is surfaced in the coordinator's view after this many days.",
    schema: z.number().int().min(1).max(365),
    default: 7,
  }),
  "seals.rate_per_seal": def({
    key: "seals.rate_per_seal",
    group: "Seals",
    label: "Rate per seal",
    description: "One general figure: what a customer is charged per seal applied (productions, esrogim) or assigned (tzitzis). The rate that applied is kept with each charge.",
    schema: z.number().min(0).max(100),
    default: 0.25,
    consequence: "Changes every seal charge raised from now on; existing charges keep their rate.",
  }),
  "seals.tzitzis_default_shipping": def({
    key: "seals.tzitzis_default_shipping",
    group: "Seals",
    label: "Default shipping on a tzitzis roll",
    description: "Added to the per-seal charge when a roll is assigned to a company; editable on the assignment.",
    schema: z.number().min(0).max(10_000),
    default: 12,
  }),
  "seals.nearly_exhausted_pct": def({
    key: "seals.nearly_exhausted_pct",
    group: "Seals",
    label: "Nearly exhausted at (% remaining)",
    description: "A roll with this much or less remaining shows on the alerts list.",
    schema: z.number().int().min(1).max(50),
    default: 10,
  }),
  "seals.void_alert_pct": def({
    key: "seals.void_alert_pct",
    group: "Seals",
    label: "Void alert (% of a roll)",
    description: "A roll whose voids exceed this share of its numbers shows on the alerts list.",
    schema: z.number().int().min(1).max(100),
    default: 5,
  }),
  "seals.unused_after_days": def({
    key: "seals.unused_after_days",
    group: "Seals",
    label: "Assigned but unused after (days)",
    description: "A roll assigned this long ago with no event against it shows on the alerts list.",
    schema: z.number().int().min(1).max(365),
    default: 60,
  }),
  "billing.default_payment_terms_days": def({
    key: "billing.default_payment_terms_days",
    group: "Billing",
    label: "Default payment terms (days)",
    description: "Due date = issue date + this, unless the fee or the company says otherwise.",
    schema: z.number().int().min(0).max(180),
    default: 30,
  }),
  "billing.invoice_number_prefix": def({
    key: "billing.invoice_number_prefix",
    group: "Billing",
    label: "Invoice number prefix",
    description: "Invoices are numbered PREFIX-YYYY-NNNN from one sequence.",
    schema: z.string().min(1).max(10),
    default: "INV",
  }),
  "billing.appointment_expiry_days": def({
    key: "billing.appointment_expiry_days",
    group: "Billing",
    label: "Invoice appointment expires after (days)",
    description: "How long the mashgiach's sign-in-free invoice link stays valid.",
    schema: z.number().int().min(1).max(365),
    default: 30,
  }),
  "billing.appointment_reminder_days": def({
    key: "billing.appointment_reminder_days",
    group: "Billing",
    label: "Remind about an appointment after (days)",
    description: "If no invoice arrives, the mashgiach is reminded once after this many days.",
    schema: z.number().int().min(1).max(90),
    default: 7,
  }),
  "billing.qbo_max_attempts": def({
    key: "billing.qbo_max_attempts",
    group: "Integrations",
    label: "QuickBooks push attempts",
    description: "How many times a push is retried with back-off before it is marked failed and shown on the reconciliation view.",
    schema: z.number().int().min(1).max(20),
    default: 5,
  }),
  "billing.ap_fronting_days": def({
    key: "billing.ap_fronting_days",
    group: "Billing",
    label: "Front mashgiach pay after (days)",
    description: "CRC pays a mashgiach for a production visit after this many days even if the customer has not paid.",
    schema: z.number().int().min(1).max(365),
    default: 40,
    consequence: "Money CRC is fronting appears on the AP-over report.",
  }),
  "certificates.default_validity_months": def({
    key: "certificates.default_validity_months",
    group: "Certificates",
    label: "Default certificate validity (months)",
    description: "Used when the company has no contract end date.",
    schema: z.number().int().min(1).max(60),
    default: 12,
  }),
  "certificates.renew_ahead_days": def({
    key: "certificates.renew_ahead_days",
    group: "Certificates",
    label: "Renew certificates this many days before expiry",
    description: "The daily renewal job re-issues an active company's certificate within this window under a new number; the old one expires on its own date.",
    schema: z.number().int().min(1).max(365),
    default: 30,
    consequence: "A company that is suspended, terminated or past its contract end is not renewed — its coordinator is told instead.",
  }),
  "certificates.number_prefix": def({
    key: "certificates.number_prefix",
    group: "Certificates",
    label: "Certificate number prefix",
    description: "One sequence with a configurable prefix; numbers are never reused.",
    schema: z.string().max(10),
    default: "VK",
  }),
  "imports.company_status_map": def({
    key: "imports.company_status_map",
    group: "Imports",
    label: "Legacy company status → platform status",
    description: "How a status word in an imported file (the legacy portal's Active / Inactive / Pending, a ClickUp stage) becomes a platform company status. Keys are the legacy words, lower-case; values are platform statuses: prospect, onboarding, certified, suspended, terminated, follow_client_feedback. A file may also carry the platform status itself. A word with no translation rejects the row with the reason.",
    schema: z.record(z.string(), z.enum(COMPANY_STATUSES)),
    default: { active: "certified", inactive: "terminated", pending: "prospect", closed: "terminated" } as Record<string, (typeof COMPANY_STATUSES)[number]>,
    consequence: "An imported company's status decides what its users can do and whether it appears in the certified lists — a wrong translation certifies or terminates hundreds of companies at once.",
  }),
  "imports.ingredient_status_map": def({
    key: "imports.ingredient_status_map",
    group: "Imports",
    label: "Legacy company ingredient status → platform status",
    description: "How the approval word on a company ingredient in an imported file (the legacy portal's Approved, Approved - KFP, Unassigned, Rejected, See Notes…) becomes a platform company-ingredient status. Keys are the legacy words, lower-case, without the portal's 'Updated by …' trailer or its '- KFP' / '- Not KFP' Passover suffix (those are read separately); values are platform statuses: pending, under_review, approved, rejected. A word with no translation rejects the row with the reason.",
    schema: z.record(z.string(), z.enum(IMPORTABLE_INGREDIENT_STATUSES)),
    default: { approved: "approved", "approved only for non-food": "approved", unassigned: "pending", pending: "pending", rejected: "rejected", "see notes": "under_review" } as Record<string, (typeof IMPORTABLE_INGREDIENT_STATUSES)[number]>,
    consequence: "A company ingredient's status decides whether it may be used in products and productions — a wrong translation approves or rejects thousands of ingredients at once.",
  }),
  "imports.product_default_status": def({
    key: "imports.product_default_status",
    group: "Imports",
    label: "Status of an imported product that carries none",
    description: "The approval status given to a product an imported file brings without one. The legacy portal keeps no approval status on a product — every product it lists is a certified product on a certificate — so the default is approved; set pending to have them reviewed first.",
    schema: z.enum(["approved", "pending"]),
    default: "approved" as "approved" | "pending",
    consequence: "An approved product appears on certificates and in the certified lists; a pending one waits for a review.",
  }),
  "imports.plant_link_status_map": def({
    key: "imports.plant_link_status_map",
    group: "Imports",
    label: "Legacy company ↔ plant link status → platform link status",
    description: "How the status word on a company ↔ plant link in an imported file (the legacy portal's Active / Not Active on a plant's company grid) becomes a platform link status. Keys are the legacy words, lower-case; values are platform link statuses: pending, linked, ended. A file may also carry the platform status itself. A word with no translation rejects the row with the reason.",
    schema: z.record(z.string(), z.enum(PLANT_LINK_STATUSES)),
    default: { active: "linked", "not active": "ended", inactive: "ended", pending: "pending" } as Record<string, (typeof PLANT_LINK_STATUSES)[number]>,
    consequence: "A link's status decides whether a company may schedule productions at the plant — a wrong translation opens or closes hundreds of plant relationships at once.",
  }),
  "maps.provider": def({
    key: "maps.provider",
    group: "Maps",
    label: "Map provider",
    description: "leaflet: the free map (OpenStreetMap streets, Esri satellite) — no account. google: Google Maps, needs GOOGLE_MAPS_BROWSER_KEY on the API; without it the free map is shown and this page says so.",
    schema: z.enum(["leaflet", "google"]),
    default: "leaflet",
  }),
  "maps.street_tile_url": def({
    key: "maps.street_tile_url",
    group: "Maps",
    label: "Street map tiles",
    description: "Tile URL template for the live map's street layer ({z}/{x}/{y}). OpenStreetMap by default — no account needed; swap in a paid provider by pasting its template.",
    schema: z.string().url().max(500),
    default: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  }),
  "maps.street_attribution": def({
    key: "maps.street_attribution",
    group: "Maps",
    label: "Street map attribution",
    description: "Shown on the map, as the provider's terms require.",
    schema: z.string().max(300),
    default: "© OpenStreetMap contributors",
  }),
  "maps.satellite_tile_url": def({
    key: "maps.satellite_tile_url",
    group: "Maps",
    label: "Satellite tiles",
    description: "Tile URL template for the aerial layer — the one orchards are drawn on. Esri World Imagery by default.",
    schema: z.string().url().max(500),
    default: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  }),
  "maps.satellite_labels_url": def({
    key: "maps.satellite_labels_url",
    group: "Maps",
    label: "Satellite: place names layer",
    description: "Tile URL template drawn over the satellite layer for country, city and place names and boundaries. Blank for none.",
    schema: z.union([z.literal(""), z.string().url().max(500)]),
    default: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
  }),
  "maps.satellite_roads_url": def({
    key: "maps.satellite_roads_url",
    group: "Maps",
    label: "Satellite: roads layer",
    description: "Tile URL template drawn over the satellite layer for roads and streets. Blank for none.",
    schema: z.union([z.literal(""), z.string().url().max(500)]),
    default: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
  }),
  "maps.satellite_attribution": def({
    key: "maps.satellite_attribution",
    group: "Maps",
    label: "Satellite attribution",
    description: "Shown on the map, as the provider's terms require.",
    schema: z.string().max(300),
    default: "Tiles © Esri — Esri, Maxar, Earthstar Geographics, and the GIS User Community",
  }),
  "sequences.certificate_floor": def({
    key: "sequences.certificate_floor",
    group: "Sequences",
    label: "Certificate numbers continue above",
    description: "The platform never issues a certificate number at or below this. Before cutover, set it to the highest number the legacy system ever issued — voided ones included.",
    schema: z.number().int().min(0).max(100_000_000),
    default: 1000,
    consequence: "A number at or below the floor could collide with a certificate that exists on paper.",
  }),
  "sequences.production_floor": def({
    key: "sequences.production_floor",
    group: "Sequences",
    label: "Production numbers continue above",
    description: "The platform never issues a production number at or below this. Set it above the highest legacy number, reserved ones included.",
    schema: z.number().int().min(0).max(100_000_000),
    default: 16000,
    consequence: "Mashgichim and companies quote production numbers; a reused one points at two visits.",
  }),
  "sequences.mashgiach_invoice_floor": def({
    key: "sequences.mashgiach_invoice_floor",
    group: "Sequences",
    label: "Mashgiach invoice numbers continue above",
    description: "The platform never issues a mashgiach invoice number at or below this. Set it above the highest legacy invoice number.",
    schema: z.number().int().min(0).max(100_000_000),
    default: 607000,
    consequence: "A reused number is two payments with one reference in QuickBooks.",
  }),
  "sequences.invoice_floor": def({
    key: "sequences.invoice_floor",
    group: "Sequences",
    label: "Customer invoice suffix continues above",
    description: "Platform invoices are numbered prefix-year-NNNN; this year's suffix continues above this number as well as above the highest already issued.",
    schema: z.number().int().min(0).max(1_000_000),
    default: 0,
  }),
  "practitioners.credential_warning_days": def({
    key: "practitioners.credential_warning_days",
    group: "People",
    label: "Credential expiry warning (days)",
    description: "A kabbalah or other credential lapsing within this many days goes into the STaM desk's digest.",
    schema: z.number().int().min(1).max(365),
    default: 60,
  }),
  "people.duplicate_similarity": def({
    key: "people.duplicate_similarity",
    group: "People",
    label: "Duplicate-name similarity",
    description: "How alike two names must be (0–1, trigram similarity) to be proposed as one person. Lower finds more, with more false pairs.",
    schema: z.number().min(0.2).max(1),
    default: 0.55,
  }),
  "monitoring.queue_depth_alert": def({
    key: "monitoring.queue_depth_alert",
    group: "Monitoring",
    label: "Alert when the outbox holds this many",
    description: "Queued notifications waiting for the tick. Administrators are told once a day while it stays above.",
    schema: z.number().int().min(1).max(100_000),
    default: 500,
  }),
  "monitoring.failed_notifications_alert": def({
    key: "monitoring.failed_notifications_alert",
    group: "Monitoring",
    label: "Alert at this many delivery failures in 24 hours",
    description: "Bounces and provider errors.",
    schema: z.number().int().min(1).max(10_000),
    default: 10,
  }),
  "monitoring.qbo_failed_alert": def({
    key: "monitoring.qbo_failed_alert",
    group: "Monitoring",
    label: "Alert at this many failed QuickBooks jobs",
    description: "Jobs that exhausted their retries and need a person.",
    schema: z.number().int().min(1).max(10_000),
    default: 1,
  }),
  "monitoring.sola_failed_alert": def({
    key: "monitoring.sola_failed_alert",
    group: "Monitoring",
    label: "Alert at this many failed card charges in 24 hours",
    description: "Declines are normal in ones; a run of them is a problem with Sola or the run.",
    schema: z.number().int().min(1).max(10_000),
    default: 3,
  }),
  "monitoring.import_stuck_hours": def({
    key: "monitoring.import_stuck_hours",
    group: "Monitoring",
    label: "An import is stuck after (hours)",
    description: "A job still running this long is not going to finish by itself.",
    schema: z.number().int().min(1).max(168),
    default: 2,
  }),
  "notifications.reply_to": def({
    key: "notifications.reply_to",
    group: "Notifications",
    label: "Reply-to address (general)",
    description: "Where replies to platform emails land. Empty = the sending address.",
    schema: z.string().max(200),
    default: "",
  }),
  "notifications.reply_to_billing": def({
    key: "notifications.reply_to_billing",
    group: "Notifications",
    label: "Reply-to address (billing)",
    description: "Replies to invoices, reminders and overdue notices are routed here.",
    schema: z.string().max(200),
    default: "",
  }),
  "billing.collections_bcc": def({
    key: "billing.collections_bcc",
    group: "Billing",
    label: "Collections BCC",
    description: "Blind-copied on every overdue notice, payment failure and overdue warning. Empty = nobody.",
    schema: z.string().max(200),
    default: "",
  }),
  "notifications.unresolved_comment_days": def({
    key: "notifications.unresolved_comment_days",
    group: "Notifications",
    label: "Unresolved comment reminder (days)",
    description: "A thread still unresolved after this many days goes into its author's digest.",
    schema: z.number().int().min(1).max(90),
    default: 7,
  }),
  "notifications.weekly_digest_day": def({
    key: "notifications.weekly_digest_day",
    group: "Notifications",
    label: "Weekly digest day",
    description: "Day of the week (0 = Sunday) the weekly digest goes out; moved if it falls in a blackout.",
    schema: z.number().int().min(0).max(6),
    default: 0,
  }),
  "notifications.timezone": def({
    key: "notifications.timezone",
    group: "Notifications",
    label: "Time zone",
    description: "For digest times and the day boundary.",
    schema: z.string().min(1).max(60),
    default: "America/New_York",
  }),
  "notifications.location": def({
    key: "notifications.location",
    group: "Notifications",
    label: "Location for sunset (lat, lng)",
    description: "Shabbos and Yom Tov windows are derived from sunset here. Default: Brooklyn.",
    schema: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }),
    default: { lat: 40.67, lng: -73.94 },
    consequence: "Moves every blackout window; nothing sends on Shabbos or Yom Tov.",
  }),
  "notifications.candle_lighting_minutes": def({
    key: "notifications.candle_lighting_minutes",
    group: "Notifications",
    label: "Blackout starts before sunset (minutes)",
    description: "Candle-lighting margin on the eve.",
    schema: z.number().int().min(0).max(120),
    default: 18,
  }),
  "notifications.nightfall_minutes": def({
    key: "notifications.nightfall_minutes",
    group: "Notifications",
    label: "Blackout ends after sunset (minutes)",
    description: "Nightfall margin at the end.",
    schema: z.number().int().min(0).max(120),
    default: 50,
  }),
  "notifications.digest_hour": def({
    key: "notifications.digest_hour",
    group: "Notifications",
    label: "Daily digest hour (local)",
    description: "Hour of the day digests are assembled and sent.",
    schema: z.number().int().min(0).max(23),
    default: 7,
  }),
  "notifications.invoice_reminder_hold_minutes": def({
    key: "notifications.invoice_reminder_hold_minutes",
    group: "Notifications",
    label: "Invoice reminder hold (minutes)",
    description: "Delay before an invoice reminder is sent, so a reminder can be cancelled if the invoice is filed.",
    schema: z.number().int().min(0).max(24 * 60),
    default: 5,
  }),
  "lists.skeleton_delay_ms": def({
    key: "lists.skeleton_delay_ms",
    group: "Interface",
    label: "Skeleton delay (ms)",
    description: "Loading skeletons appear after this delay, never before.",
    schema: z.number().int().min(0).max(2000),
    default: 200,
  }),
  "compliance.default_severity": def({
    key: "compliance.default_severity",
    group: "Compliance",
    label: "Default issue severity",
    description: "Severity assigned when a report raises an issue and none was chosen.",
    schema: z.string(),
    default: "minor",
  }),
  "membership.member_number_prefix": def({
    key: "membership.member_number_prefix",
    group: "Membership",
    label: "Member number prefix",
    description: "Member numbers are issued in sequence as <prefix><number>, e.g. M-0001.",
    schema: z.string().max(10),
    default: "M-",
  }),
  "membership.retry_days": def({
    key: "membership.retry_days",
    group: "Membership",
    label: "Days between retries",
    description: "After a declined membership charge, the next attempt waits this many days.",
    schema: z.number().int().min(1).max(30),
    default: 3,
    consequence: "A shorter wait charges the member's card again sooner.",
  }),
  "membership.max_attempts": def({
    key: "membership.max_attempts",
    group: "Membership",
    label: "Attempts per charge",
    description: "How many times the scheduler tries a membership charge before leaving it for a person.",
    schema: z.number().int().min(1).max(10),
    default: 3,
    consequence: "After the last attempt nothing retries by itself; the charge waits on the Charges tab.",
  }),
  "membership.grace_days": def({
    key: "membership.grace_days",
    group: "Membership",
    label: "Grace period (days)",
    description: "A membership charge unpaid this many days after its due date lapses the subscription, and the member when no other subscription keeps them active.",
    schema: z.number().int().min(0).max(365),
    default: 14,
    consequence: "Lapsing stops charging; the member is marked lapsed until a person or a payment reactivates them.",
  }),
  "membership.alert_on_failure": def({
    key: "membership.alert_on_failure",
    group: "Membership",
    label: "Alert accounting on a declined charge",
    description: "Send the payment-failed notice to accounting and administrators when a membership charge is declined.",
    schema: z.boolean(),
    default: true,
  }),
  "membership.portal_session_days": def({
    key: "membership.portal_session_days",
    group: "Membership",
    label: "Member portal session (days)",
    description: "How long a member stays signed in to the portal.",
    schema: z.number().int().min(1).max(365),
    default: 30,
  }),
  "membership.portal_invite_days": def({
    key: "membership.portal_invite_days",
    group: "Membership",
    label: "Portal invitation validity (days)",
    description: "How long a portal invitation or password-reset link stays usable.",
    schema: z.number().int().min(1).max(90),
    default: 14,
  }),
  "membership.question_number_prefix": def({
    key: "membership.question_number_prefix",
    group: "Membership",
    label: "Question number prefix",
    description: "Member questions are numbered <prefix><year>-<sequence>, e.g. CRC-2026-10482.",
    schema: z.string().max(10),
    default: "CRC-",
  }),
  "membership.question_answer_estimate": def({
    key: "membership.question_answer_estimate",
    group: "Membership",
    label: "Promised answer time",
    description: "The line every question email and page carries, like a delivery estimate.",
    schema: z.string().min(1).max(200),
    default: "approximately 24 hours (Sunday–Thursday)",
  }),
  "membership.office_contact": def({
    key: "membership.office_contact",
    group: "Membership",
    label: "How members contact the office",
    description: "Shown under \"Contact the office\" in the member portal and at the foot of member emails.",
    schema: z.string().min(1).max(500),
    default: "Call the CRC office during office hours, or reply to any email you received about your question.",
  }),
} as const;

export type SettingKey = keyof typeof SETTINGS;
export const SETTING_KEYS = Object.keys(SETTINGS) as SettingKey[];

export function settingDefault<K extends SettingKey>(key: K): (typeof SETTINGS)[K]["default"] {
  return SETTINGS[key].default;
}
